// Onglet Lieux (sites) - Design moderne avec Cards

import React, { useState } from 'react';
import { 
  Button, 
  Tag, 
  message, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Switch, 
  Alert,
  Card,
  Space,
  Typography,
  Row,
  Col,
  Empty,
  Spin,
  Dropdown,
  Menu,
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  SyncOutlined,
  ShopOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  EnvironmentOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useSites, useAddSite, useUpdateSite, useDeleteSite } from '../../../hooks/useSites';
import { useBackfillSaleMovementCosts } from '../../../hooks/useSuperAdmin';
import { useBusiness } from '../../../hooks/useBusinesses';
import { canSellFromAnySite, getSalesSiteUiLabels } from '../../../types/business';
import { Site, SiteType, SITE_TYPE_LABELS } from '../../../types/site';
import './SitesTabContent.css';

const { Text } = Typography;

interface SitesTabContentProps {
  businessId: string;
}

const SitesTabContent: React.FC<SitesTabContentProps> = ({ businessId }) => {
  const { data: business } = useBusiness(businessId);
  const sellFromAnySite = canSellFromAnySite(business ?? null);
  const siteLabels = getSalesSiteUiLabels(business ?? null);
  const { data: sites = [], isLoading } = useSites(businessId);
  const pointOfSaleCount = sites.filter((s) => s.type === 'point_of_sale').length;
  const addSite = useAddSite();
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();
  const backfillSaleMovementCosts = useBackfillSaleMovementCosts();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [deletingSiteId, setDeletingSiteId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const handleAdd = () => {
    setEditingSite(null);
    form.setFieldsValue({ name: '', type: 'warehouse' as SiteType, isActive: true });
    setModalOpen(true);
  };

  const handleEdit = (site: Site) => {
    setEditingSite(site);
    form.setFieldsValue({ name: site.name, type: site.type, isActive: site.isActive });
    setModalOpen(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingSite) {
        await updateSite.mutateAsync({
          siteId: editingSite.id,
          businessId,
          name: values.name,
          type: values.type,
          isActive: values.isActive,
          allowMultiplePointOfSale: sellFromAnySite,
        });
        message.success('Lieu mis à jour');
      } else {
        await addSite.mutateAsync({
          businessId,
          name: values.name,
          type: values.type,
          allowMultiplePointOfSale: sellFromAnySite,
        });
        message.success('Lieu créé');
      }
      setModalOpen(false);
      form.resetFields();
    } catch (e: any) {
      if (e.errorFields) return;
      message.error(e.message || 'Erreur');
    }
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    setEditingSite(null);
    form.resetFields();
  };

  const handleBackfillSaleMovementCosts = () => {
    Modal.confirm({
      title: 'Rattrapage des coûts de ventes',
      content: (
        <div>
          <p>
            Cette action va compléter les mouvements <strong>&quot;Vente produit&quot;</strong> qui n&apos;ont pas de coût.
          </p>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>
              <strong>Coût unitaire</strong> : <code>averageCost</code> du produit (fallback <code>costPrice</code>).
            </li>
            <li>
              <strong>Coût total</strong> : <code>coût unitaire x quantité</code>.
            </li>
            <li>Les mouvements déjà complets ne sont pas modifiés.</li>
          </ul>
        </div>
      ),
      okText: 'Lancer le rattrapage',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const result = await backfillSaleMovementCosts.mutateAsync({ businessId });
          message.success(
            `Rattrapage terminé : ${result.updated} mouvement(s) mis à jour (candidats ${result.candidates}, produits introuvables ${result.missingProducts}).`
          );
        } catch (e: any) {
          message.error(e.message || 'Erreur lors du rattrapage');
        }
      },
    });
  };

  const handleToggleSite = async (site: Site, checked: boolean) => {
    if (site.type === 'point_of_sale' && !checked && !sellFromAnySite) {
      message.warning('Le point de vente principal ne peut pas être désactivé.');
      return;
    }
    try {
      await updateSite.mutateAsync({
        siteId: site.id,
        businessId,
        isActive: checked,
        allowMultiplePointOfSale: sellFromAnySite,
      });
      message.success(checked ? 'Lieu activé' : 'Lieu désactivé');
    } catch (e: any) {
      message.error(e.message || 'Erreur');
    }
  };

  const handleDeleteSite = (site: Site) => {
    Modal.confirm({
      title: `Supprimer « ${site.name} » ?`,
      content: (
        <div>
          <p>
            La suppression est définitive. Elle n’est possible que s’il n’y a{' '}
            <strong>aucune vente</strong>, <strong>aucun stock produit</strong> et{' '}
            <strong>aucun historique</strong> (mouvements, approvisionnements, inventaires…)
            sur ce lieu.
          </p>
          <p style={{ marginBottom: 0 }}>
            Sinon, désactivez le lieu. Les références mortes sûres (favoris employés, clés stock à 0)
            seront nettoyées automatiquement si la suppression est autorisée.
          </p>
        </div>
      ),
      okText: 'Supprimer',
      okButtonProps: { danger: true },
      cancelText: 'Annuler',
      onOk: async () => {
        setDeletingSiteId(site.id);
        try {
          await deleteSite.mutateAsync({
            siteId: site.id,
            businessId,
            allowMultiplePointOfSale: sellFromAnySite,
          });
          message.success('Lieu supprimé');
        } catch (e: any) {
          message.error(e?.message || 'Suppression impossible');
          throw e;
        } finally {
          setDeletingSiteId(null);
        }
      },
    });
  };

  // Rendu d'une card de site
  const renderSiteCard = (site: Site) => {
    const menu = (
      <Menu>
        <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEdit(site)}>
          Modifier
        </Menu.Item>
        <Menu.Item
          key="delete"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDeleteSite(site)}
        >
          Supprimer
        </Menu.Item>
      </Menu>
    );

    const siteIcon = site.type === 'point_of_sale' ? <ShopOutlined /> : <InboxOutlined />;
    const siteColor = site.type === 'point_of_sale' ? '#52c41a' : '#1890ff';

    return (
      <Card 
        key={site.id}
        className="site-card"
        hoverable
        style={{
          marginBottom: '16px',
          borderLeft: `4px solid ${site.isActive ? siteColor : '#d9d9d9'}`,
        }}
      >
        <Row gutter={16} align="middle">
          {/* Infos principales */}
          <Col xs={24} sm={24} md={16}>
            <Space size={16} align="start">
              {/* Icône */}
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${siteColor} 0%, ${siteColor}dd 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}>
                <span style={{ fontSize: '24px', color: 'white' }}>
                  {siteIcon}
                </span>
              </div>

              {/* Infos */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: '8px' }}>
                  <Space wrap size={[8, 4]}>
                    <Text strong style={{ fontSize: '16px' }}>{site.name}</Text>
                    <Tag color={site.type === 'point_of_sale' ? 'green' : 'blue'}>
                      {SITE_TYPE_LABELS[site.type]}
                    </Tag>
                    <Tag color={site.isActive ? 'green' : 'red'} icon={site.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                      {site.isActive ? 'Actif' : 'Inactif'}
                    </Tag>
                  </Space>
                </div>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {site.type === 'point_of_sale'
                    ? sellFromAnySite
                      ? `📍 ${siteLabels.pointOfSaleType} — utilisable pour les ventes`
                      : '📍 Point de vente - Utilisé pour les transactions clients'
                    : sellFromAnySite
                      ? '📦 Entrepôt / dépôt — réception et sortie stock vente possible'
                      : '📦 Entrepôt - Réception des approvisionnements'}
                </Text>
              </div>
            </Space>
          </Col>

          {/* Actions */}
          <Col xs={24} sm={24} md={8}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '12px',
              marginTop: '12px'
            }}>
              <Switch
                checked={site.isActive}
                onChange={(checked) => handleToggleSite(site, checked)}
                checkedChildren="Actif"
                unCheckedChildren="Inactif"
                disabled={site.type === 'point_of_sale' && site.isActive && !sellFromAnySite}
              />
              <Space size={8}>
                <Button
                  type="default"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(site)}
                >
                  Modifier
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingSiteId === site.id}
                  onClick={() => handleDeleteSite(site)}
                >
                  Supprimer
                </Button>
                <Dropdown overlay={menu} trigger={['click']}>
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div className="sites-tab-content">
      {/* En-tête */}
      <Card className="header-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Text strong style={{ fontSize: '16px', display: 'block' }}>
              <EnvironmentOutlined /> Gestion des lieux
            </Text>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              {sellFromAnySite
                ? 'Mode « Tous les lieux » : chaque vente peut sortir le stock depuis n’importe quel lieu actif.'
                : 'Gérez les différents lieux (magasins, entrepôts, boutiques)'}
            </Text>
          </div>
          <Space wrap>
            <Button
              icon={<SyncOutlined />}
              onClick={handleBackfillSaleMovementCosts}
              loading={backfillSaleMovementCosts.isPending}
              style={{ borderColor: '#722ed1', color: '#722ed1' }}
            >
              Rattraper coûts
            </Button>
            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleAdd}>
              Ajouter un lieu
            </Button>
          </Space>
        </div>
      </Card>

      {/* Alerte info */}
      {sites.length === 0 && (
        <Alert
          message="Aucun lieu configuré"
          description={
            sellFromAnySite
              ? 'Les lieux permettent de gérer plusieurs dépôts ou entrepôts. Ajoutez au moins un lieu actif pour commencer.'
              : 'Les lieux permettent de gérer plusieurs magasins ou entrepôts. Ajoutez au moins un point de vente pour commencer.'
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Compteur */}
      <div style={{ marginBottom: '16px' }}>
        <Text type="secondary">
          {sites.length} lieu{sites.length > 1 ? 'x' : ''} configuré{sites.length > 1 ? 's' : ''}
        </Text>
      </div>

      {/* Liste des sites */}
      <Spin spinning={isLoading} size="large">
        {sites.length === 0 ? (
          <Card>
            <Empty 
              description="Aucun lieu configuré"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Ajouter votre premier lieu
              </Button>
            </Empty>
          </Card>
        ) : (
          <div className="sites-list">
            {sites.map(site => renderSiteCard(site))}
          </div>
        )}
      </Spin>

      <Modal
        title={
          <Space>
            <EnvironmentOutlined />
            <span>{editingSite ? 'Modifier le lieu' : 'Nouveau lieu'}</span>
          </Space>
        }
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={addSite.isPending || updateSite.isPending}
        destroyOnClose
        okText={editingSite ? 'Mettre à jour' : 'Créer'}
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nom du lieu"
            rules={[{ required: true, message: 'Nom requis' }]}
          >
            <Input placeholder="ex. Magasin central, Boutique centre-ville" size="large" />
          </Form.Item>
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true }]}
            tooltip="Point de vente = lieu utilisé pour les ventes (stock de vente). Entrepôt = réception des approvisionnements. Si « Tous les lieux » est activé, tout lieu actif peut servir à la vente."
          >
            <Select
              size="large"
              options={[
                { 
                  value: 'warehouse', 
                  label: (
                    <Space>
                      <InboxOutlined />
                      {SITE_TYPE_LABELS.warehouse}
                    </Space>
                  )
                },
                {
                  value: 'point_of_sale',
                  label: (
                    <Space>
                      <ShopOutlined />
                      {sellFromAnySite ? siteLabels.pointOfSaleType : SITE_TYPE_LABELS.point_of_sale}
                    </Space>
                  ),
                  disabled: !sellFromAnySite && pointOfSaleCount > 0 && editingSite?.type !== 'point_of_sale',
                },
              ]}
            />
          </Form.Item>
          {!sellFromAnySite && pointOfSaleCount > 0 && editingSite?.type !== 'point_of_sale' && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="En mode « Boutique seule », un seul point de vente est autorisé par business."
            />
          )}
          {sellFromAnySite && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Mode « Tous les lieux » : plusieurs lieux peuvent servir à la vente. Le type (entrepôt / point de vente) reste informatif."
            />
          )}
          {editingSite && (
            <Form.Item name="isActive" label="Actif" valuePropName="checked">
              <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default SitesTabContent;
